import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Mail, Lock } from 'lucide-react';
import LiquidGlassLanguageSelect from '../components/ui/LiquidGlassLanguageSelect';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithCredentials } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { getThemedStyles } = useTheme();
  const navigate = useNavigate();
  
  const styles = getThemedStyles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { isFirstLogin } = await loginWithCredentials(email, password);
      toast.success(isFirstLogin ? t('welcome') : t('welcomeBack'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center md:p-4" style={{ backgroundColor: styles.background }}>
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20 hidden md:block"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1543094152-7f8122e97b66?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
      />
      <div className="absolute inset-0 hidden md:block" style={{ background: `linear-gradient(to top, ${styles.background}, ${styles.background}cc, transparent)` }} />
      
      {/* Language Selector - Top Right (Mobile) */}
      <div className="absolute top-4 right-4 z-10 md:hidden">
        <LiquidGlassLanguageSelect
          value={language}
          onValueChange={setLanguage}
          testId="language-select-mobile"
          options={[
            { code: 'en', label: t('english') },
            { code: 'pt', label: t('portuguese') },
            { code: 'fr', label: t('french') },
            { code: 'es', label: t('spanish') },
            { code: 'el', label: t('greek') },
            { code: 'ru', label: t('russian') },
            { code: 'it', label: t('italian') },
          ]}
        />
      </div>
      
      <div className="relative w-full max-w-lg">
        <div className="min-h-screen md:min-h-0 md:rounded-2xl p-6 md:p-8 md:border flex flex-col justify-center" style={{ backgroundColor: styles.surface, borderColor: styles.secondary }}>
          {/* Language Selector - Inside card (Desktop) */}
          <div className="hidden md:flex justify-end mb-4">
            <LiquidGlassLanguageSelect
              value={language}
              onValueChange={setLanguage}
              options={[
                { code: 'en', label: t('english') },
                { code: 'pt', label: t('portuguese') },
                { code: 'fr', label: t('french') },
                { code: 'es', label: t('spanish') },
                { code: 'el', label: t('greek') },
                { code: 'ru', label: t('russian') },
                { code: 'it', label: t('italian') },
              ]}
            />
          </div>
          
          <div className="text-center mb-8 md:mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2" style={{ color: styles.primary, letterSpacing: '2px' }}>Sitoa</h1>
            <p className="text-sm md:text-base italic" style={{ color: styles.textMuted }}>from yes to welcome</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base" style={{ color: styles.textMuted }}>{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 py-6 text-base rounded-xl"
                  style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base" style={{ color: styles.textMuted }}>{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: styles.textMuted }} />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 py-6 text-base rounded-xl"
                  style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary, borderColor: styles.secondary, color: styles.text }}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full font-semibold rounded-xl py-6 text-base md:text-lg transition-all duration-300"
              style={{ backgroundColor: styles.primary, color: styles.mode === 'light' ? '#ffffff' : styles.background }}
              data-testid="login-submit-btn"
            >
              <LogIn className="mr-2 h-5 w-5" />
              {loading ? 'Loading...' : t('login')}
            </Button>

            {/* Google Login - HIDDEN: Temporarily disabled pending custom OAuth setup */}
            {false && <>
            {/* Divider */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: styles.secondary }} />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3" style={{ backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.card, color: styles.textMuted }}>{t('or') || 'or'}</span>
              </div>
            </div>

            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <Button
              type="button"
              variant="outline"
              className="w-full font-semibold rounded-xl py-6 text-base transition-all duration-300 flex items-center justify-center gap-3"
              style={{ borderColor: styles.secondary, color: styles.primary, backgroundColor: styles.mode === 'light' ? '#ffffff' : styles.secondary }}
              onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-login-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t('signInWithGoogle') || 'Sign in with Google'}
            </Button>
            </>}
            
            <p className="text-center mt-4 text-base" style={{ color: styles.textMuted }}>
              <Link to="/forgot-password" className="hover:underline" style={{ color: styles.primary }}>
                {t('forgotPassword')}
              </Link>
            </p>
            
            <p className="text-center mt-6 text-base" style={{ color: styles.textMuted }}>
              {t('dontHaveAccount')}{' '}
              <Link to="/register" style={{ color: styles.primary }} data-testid="register-link">
                {t('register')}
              </Link>
            </p>
          </form>
          
          <p className="text-center text-sm mt-6" style={{ color: styles.textMuted }}>
            {t('bySigningIn')}{' '}
            <Link to="/terms" className="hover:underline" style={{ color: styles.primary }}>{t('terms')}</Link>
            {' '}{t('and')}{' '}
            <Link to="/privacy" className="hover:underline" style={{ color: styles.primary }}>{t('privacyPolicy')}</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
